// multi-email-input.component.ts
import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-multi-email-input',
  templateUrl: './multi-email-input.component.html',
  styles: [
    `
      .limit-reached {
        border-color: #ef4444 !important;
        box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.2) !important;
      }

      .near-limit {
        border-color: #f59e0b !important;
      }

      .character-count-container {
        margin-top: 4px;
      }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiEmailInputComponent),
      multi: true
    }
  ]
})
export class MultiEmailInputComponent implements ControlValueAccessor {
  @Input() maxLength = 500; // Maximum characters for a single email
  @Input() showCharCount = true;
  @Input() disabled = false;

  emails: string[] = [];
  currentInputLength = 0; // Length of the current input email
  currentInputValue = ''; // Current input content

  private onChange = (value: string[]) => {};
  private onTouched = () => {};

  getEmailDisplay(email: string, index: number): string {
    return index === 0 ? `${email}` : email;
  }

  // Get the length of the current input email
  getCurrentInputLength(): number {
    return this.currentInputLength;
  }

  // Check if the current input has reached the character limit
  isLimitReached(): boolean {
    return this.currentInputLength >= this.maxLength;
  }

  // Check if the current input is near the limit (for warning state)
  isNearLimit(): boolean {
    return this.currentInputLength >= this.maxLength * 0.9; // Show warning at 90%
  }

  // Check if any email exceeds the length limit
  hasEmailExceedingLimit(): boolean {
    return this.emails.some(email => email.length > this.maxLength);
  }

  // Listen for search input changes
  onSearchChange(searchValue: string): void {
    this.currentInputValue = searchValue || '';
    this.currentInputLength = this.currentInputValue.length;
  }

  // Listen for keyboard events to prevent input beyond the limit
  onKeyDown(event: KeyboardEvent): void {
    // Allowed special keys: Backspace, Delete, Arrow keys, Tab, Enter, Semicolon, Comma, etc.
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Tab',
      'Enter',
      'Escape',
      'Home',
      'End',
      'PageUp',
      'PageDown'
    ];

    // If it's an allowed special key, let it through
    if (allowedKeys.includes(event.key)) {
      return;
    }

    // If it's a separator (comma, semicolon), let it through
    if (event.key === ',' || event.key === ';') {
      return;
    }

    // If the current input has reached the maximum length, prevent further input
    if (this.currentInputLength >= this.maxLength) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
  }

  onEmailsChange(emails: string[]) {
    // Filter out emails that exceed the length limit
    const validEmails = emails
      .filter(email => email && email.trim())
      .map(email => email.trim())
      .filter(email => {
        // Check the length and format of each email
        return email.length <= this.maxLength && this.isValidEmail(email);
      });

    this.emails = validEmails;
    this.onChange(validEmails);
    this.onTouched();

    // If the emails array changes, reset the current input state
    this.currentInputValue = '';
    this.currentInputLength = 0;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  writeValue(value: string[] | string): void {
    if (typeof value === 'string') {
      // If a string is passed, split by semicolon and filter by length
      this.emails = value
        ? value
            .split(';')
            .map(e => e.trim())
            .filter(e => e && e.length <= this.maxLength)
        : [];
    } else {
      // Filter out emails that are too long
      this.emails = value
        ? value.filter(email => email.length <= this.maxLength)
        : [];
    }
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}